<template>
  <div class="rmaForm">
    <div v-if="upsLoading" class="loader loader-positioning" />
    <Radio
      v-if="
        $store.state.rma.transportSecurePacking === true ||
          $store.state.rma.transportOriginalPackaging === true
      "
      :questionString="$mlGet('rmaTransport-question-upsPickup')"
      v-on:userInput="handleUpsPickup"
      :answer="$store.state.rma.transportUpsPickup"
      :option1Text="$mlGet('rmaTransport-question-upsPickup-option1')"
      :option2Text="$mlGet('rmaTransport-question-upsPickup-option2')"
    />
    <div class="information-box">
      <p v-if="$store.state.rma.transportUpsPickup">
        <span v-html="$mlGet('rmaTransport2-informationBox-b')"></span>
      </p>
      <p v-if="!$store.state.rma.transportUpsPickup">
        <span v-html="$mlGet('rmaTransport2-informationBox-a')"></span>
      </p>
    </div>
    <div
      class="upsWrapper"
      v-if="servicePoints.length != 0 && !$store.state.rma.transportUpsPickup"
    >
      <p>
        <span v-html="$mlGet('rmaTransport2-upsresults-label')"></span>
      </p>
      <div class="table-wrapper-heightLimit">
        <table>
          <tr>
            <th>{{ $mlGet("rmaTransport2-upsresult-name-label") }}</th>
            <th>{{ $mlGet("rmaTransport2-upsresult-address-label") }}</th>
            <th>{{ $mlGet("rmaTransport2-upsresult-city-label") }}</th>
            <th>{{ $mlGet("rmaTransport2-upsresult-distance-label") }}</th>
          </tr>
          <tr
            v-for="(servicePoint, index) in servicePoints"
            :key="index"
            @click="showInfo(index)"
          >
            <td>
              {{ parseString(servicePoint.name) }}
            </td>
            <td>
              {{ parseString(servicePoint.address) }}
            </td>
            <td>
              {{ parseString(servicePoint.city) }}
            </td>
            <td>
              {{ parseString(servicePoint.distance.toFixed(1)) }}
              KM
            </td>
          </tr>
        </table>
      </div>
      <div v-if="servicePointsHover" class="servicePointInformationWrapper">
        <button @click="clearClickedPoint" class="button">
          <i class="fa fa-window-close" />
        </button>
        <h2>{{ servicePointsHover.name }}</h2>
        <b>Address:</b>
        <p>
          {{ parseString(servicePointsHover.address) }}<br />
          {{ parseString(servicePointsHover.postalCode) }}
          {{ parseString(servicePointsHover.city) }}
        </p>
      </div>
    </div>
    <div class="datesWrapper" v-if="$store.state.rma.transportUpsPickup">
      <p>
        <span v-html="$mlGet('rmaTransport2-dateresults-label')"></span>
      </p>
      <div class="table-wrapper-heightLimit">
        <table>
          <tr>
            <th>{{ $mlGet("rmaTransport2-dateresult-day-label") }}</th>
            <th>{{ $mlGet("rmaTransport2-dateresult-date-label") }}</th>
          </tr>
          <tr
            v-for="day in pickupDays"
            :key="day.id"
            @click="setRmaDate(day)"
            :class="{ clickedItem: day.active }"
          >
            <td>{{ $mlGet(day.date.day) }}</td>
            <td>
              {{ day.date.date }} {{ $mlGet(day.date.month) }}
              {{ day.date.year }}
            </td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import * as Sentry from "@sentry/vue";

import Radio from "@/components/Input/Radio.vue";
import { PageChangeContext, PageName } from "@/views/CreateRma.vue";
import { ServicePoint, TransportDate } from "../../types";

@Component({
  components: {
    Radio
  }
})
export default class RmaTransport2 extends Vue {
  public upsLoading = false;
  public servicePoints: ServicePoint[] = [];
  public servicePointsHover: ServicePoint | null = null;
  public pickupDays: Array<TransportDate> = [];

  handleUpsPickup(answer: boolean): void {
    this.$store.state.rma.transportUpsPickup = answer;

    this.$store.state.rma.upsPickupPreveredDate = "";
    this.pickupDays.map(e => Vue.set(e, "active", false));
  }

  mounted(): void {
    this.getServicePoints();
    (async () => {
      this.pickupDays = await this.getDaysCall();
      if (this.$store.state.rma.upsPickupPreveredDate) {
        this.pickupDays.map(e => Vue.set(e, "active", false));

        const searchDate = new Date(
          this.$store.state.rma.upsPickupPreveredDate
        );
        searchDate.setHours(0, 0, 0, 0);

        const dateField = this.pickupDays.find(x => {
          let response = false;
          const oldDate = new Date(x.date.js);
          oldDate.setHours(0, 0, 0, 0);

          if (searchDate.getTime() == oldDate.getTime()) response = true;
          return response;
        }) as TransportDate;

        Vue.set(dateField, "active", true);
      }
    })();
  }

  async getServicePoints(): Promise<void> {
    this.upsLoading = true;
    try {
      const response = await this.$axios.get(
        `/rma/pickupPoints?rmaId=${this.$store.state.rma.rmaId}`
      );
      const servicePoints: ServicePoint[] = response.data;

      this.servicePoints = servicePoints;
      this.upsLoading = false;
    } catch (error) {
      this.upsLoading = false;
      this.$swal({
        title: this.$mlGet("upsPickupData-error-head"),
        html: this.$mlGet("upsPickupData-error-text"),
        icon: "error"
      });
    }
  }

  async getDaysCall(): Promise<Array<TransportDate>> {
    this.upsLoading = true;
    try {
      const response = await this.$axios.get("/rma/transportDays");
      const day = response.data || [];
      this.upsLoading = false;
      return day;
    } catch (error) {
      this.upsLoading = false;
      this.$swal({
        title: this.$mlGet("getDays-error-head"),
        html: this.$mlGet("getDays-error-text"),
        icon: "error"
      });

      Sentry.captureException(error, {
        contexts: {
          getDaysCall: {
            rmaId: this.$store.state.rma.rmaId
          }
        }
      });

      this.upsLoading = false;
    }
    return [];
  }

  clearClickedPoint(): void {
    this.servicePointsHover = null;
  }

  setRmaDate(day: TransportDate): void {
    this.$store.commit("rmaSetDate", day.date.js);
    this.pickupDays.map(e => Vue.set(e, "active", false));
    Vue.set(day, "active", true);
  }

  /**
   * Returns a string where all words start with uppercase characters except for words shorter than 3 caracters
   */
  parseString(string: string): string {
    const split = string.split(" ");
    const newString: Array<string> = [];

    split.forEach(word => {
      if (word.length > 3)
        newString.push(word.charAt(0) + word.substring(1).toLowerCase());
      else newString.push(word.toLowerCase());
    });

    return newString.join(" ");
  }

  showInfo(index: number): void {
    const point = this.servicePoints[index];
    this.servicePointsHover = point;
  }

  public async beforePageLeave(context: PageChangeContext): Promise<boolean> {
    if (context.isReturnOperation) return true;

    if (
      this.$store.state.rma.transportUpsPickup &&
      !this.$store.state.rma.upsPickupPreveredDate
    ) {
      await this.$swal(
        this.$mlGet("transportInput-error-noUpsPickupDate-head"),
        this.$mlGet("transportInput-error-noUpsPickupDate-body"),
        "error"
      );
      return false;
    }
    return true;
  }

  public async afterPageEnter(context: PageChangeContext): Promise<void> {
    return;
  }

  public async onPageLeave(context: PageChangeContext): Promise<PageName> {
    return "RmaReferences";
  }
}
</script>

<style scoped>
label {
  margin: 0 20px 0 0;
}

.formRadioQuestion {
  display: block;
  padding-bottom: 2ex;
}

p {
  margin: 3px 0 8px 0;
}

.table-wrapper-heightLimit {
  max-height: 20ex;
  overflow: scroll;
  overflow-x: hidden;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  padding: 5px;
  font-size: 92%;
}

th {
  background-color: #001538;
  color: white;
  font-weight: normal;
  font-family: "Overpass", sans-serif !important;
  padding: 4px;
  padding-top: 6px;
  text-align: left;
  border-left: 1px solid gray;
  font-weight: 200;
}

td {
  border-left: 1px dotted gray;
  border-bottom: 1px dotted gray;
  cursor: pointer;
}

.upsWrapper {
  padding-top: 1.5ex;
}

.servicePointInformationWrapper {
  position: absolute;
  left: 3ex;
  top: 10ex;
  padding: 2ex;
  background-color: white;
  border: 4px solid #da6f38;
  border-radius: 4px;
  box-shadow: gray 5px 5px 1ex;
  width: 37vw;
}

.servicePointInformationWrapper img {
  float: right;
  max-width: 20ex;
  padding-top: 2ex;
}

.servicePointInformation {
  background-color: white;
  box-shadow: #333 3px 4px 10px;
  border-radius: 5px;
}

.clickedItem {
  background-color: #f0bda6 !important;
}

.button {
  border: none;
  color: #da6f38;
  background-color: white;
  font-size: 200%;
  cursor: pointer;
  float: right;
}
</style>
